import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import { pageImages, blogPosts } from '../data/pages';

const CATEGORIES = ['All', 'Bullion', 'Energy', 'Corporate Finance', 'Venture', 'Commodities', 'Capital Markets'];

const BlogPage = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return blogPosts.filter((p) => {
      const catMatch = category === 'All' || p.category === category;
      const textMatch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.author.toLowerCase().includes(q);
      return catMatch && textMatch;
    });
  }, [query, category]);

  return (
    <Layout>
      <PageHero
        eyebrow="The Emerald Blog"
        title="Insights from our desks and portfolio."
        description="Thematic research, market notes and playbooks from the people building Emerald Corporates every day."
        image={pageImages.blog}
      />

      <section className="bg-[#e2ede6] py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-10">
            <div className="relative flex-1 max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles, authors, themes…"
                className="w-full bg-white border border-[#0f5c3d]/15 rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-full border transition-colors ${
                    category === c
                      ? 'bg-[#0f5c3d] border-[#0f5c3d] text-white'
                      : 'bg-white border-[#0f5c3d]/20 text-[#0b1a2e] hover:border-[#0f5c3d]/60'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-black/5 rounded-xl p-10 text-center text-[#0b1a2e]/60">
              No articles match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article
                  key={p.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-xl transition-all"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between text-xs text-[#0b1a2e]/60">
                      <span className="font-semibold tracking-wide text-[#0f5c3d] uppercase">{p.category}</span>
                      <span>{p.date}</span>
                    </div>
                    <h3 className="mt-3 text-lg font-semibold text-[#0b1a2e] leading-snug group-hover:text-[#0f5c3d] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-sm text-[#0b1a2e]/70 leading-relaxed">{p.excerpt}</p>
                    <div className="mt-4 text-xs text-[#0b1a2e]/60">By {p.author}</div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
